import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useResetPassword } from "@/api/mutations/users";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  email: string;
  onDone: (result: { email: string; password: string }) => void;
}

export function ResetPasswordDialog({ open, onOpenChange, userId, email, onDone }: Props) {
  const [pw, setPw] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);
  const mutation = useResetPassword();

  const custom = pw.trim().length > 0;
  const tooShort = custom && pw.length < 8;

  function handleClose(o: boolean) {
    if (!o) {
      setPw("");
      setServerError(null);
    }
    onOpenChange(o);
  }

  function submit() {
    if (tooShort) return;
    setServerError(null);
    mutation.mutate(
      { id: userId, newPassword: custom ? pw : undefined },
      {
        onSuccess: ({ password }) => {
          toast.success(`Passwort für ${email} ${custom ? "gesetzt" : "generiert"}`);
          handleClose(false);
          onDone({ email, password });
        },
        onError: (err) => setServerError((err as Error).message),
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="reset-password-dialog">
        <DialogHeader>
          <DialogTitle>Passwort zurücksetzen</DialogTitle>
          <DialogDescription>
            Eigenes Passwort setzen oder das Feld leer lassen, um ein zufälliges zu generieren. Das
            alte Passwort wird sofort ungültig — der User muss das neue über einen sicheren Kanal
            erhalten.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1">
          <Label htmlFor="reset-pw">Neues Passwort (optional)</Label>
          <Input
            id="reset-pw"
            type="text"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="leer lassen → zufällig generieren"
            className="font-mono text-sm"
            data-testid="reset-password-field"
            autoFocus
          />
          {tooShort && (
            <div className="text-xs text-red" data-testid="reset-password-error">
              Mindestens 8 Zeichen.
            </div>
          )}
        </div>

        {serverError && (
          <div className="text-sm text-red" data-testid="reset-server-error">
            {serverError}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} data-testid="reset-cancel">
            Abbrechen
          </Button>
          <Button onClick={submit} disabled={mutation.isPending || tooShort} data-testid="reset-submit">
            {mutation.isPending ? "..." : custom ? "Passwort setzen" : "Zufällig generieren"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
